(() => {
  "use strict";

  // The browser half of app/semantic.py. It exists so that a phrase a viewer
  // types into their own filter never has to be sent anywhere: the server
  // sketches each public Drop once when it is published, ships that sketch with
  // the feed, and this file sketches the viewer's phrase locally and compares
  // the two. No model is downloaded, nothing is uploaded, and the server learns
  // nothing about what the viewer chose to hide.
  //
  // Every step below is the same step, in the same order, as the Python file.
  // The two are pinned against each other by tests/semantic_sketch_parity.test.cjs,
  // which sketches a shared corpus in both and requires identical strings. If a
  // change is made here it must be made there, and SKETCH_VERSION must move.
  //
  // Notes on the places where the two languages do not agree by default, all of
  // which are handled explicitly:
  //   - toLowerCase, not a casefold. Python uses str.lower for the same reason.
  //   - Iteration is by code point (for...of, Array.from), not by UTF-16 code
  //     unit, so an emoji counts as one character in both.
  //   - Rounding is half away from zero, written out, because Math.round and
  //     Python's round disagree on exact halves in opposite directions.
  //   - UTF-8 encoding and base64 are implemented here rather than borrowed from
  //     TextEncoder and btoa, so the file behaves the same in a browser, in a
  //     test sandbox, and in anything else that runs it.

  const SKETCH_VERSION = "lex1";
  const SKETCH_DIMENSIONS = 256;

  const FNV_OFFSET_BASIS = 0x811c9dc5;
  const FNV_PRIME = 0x01000193;

  // Identical literal to _SEPARATORS in app/semantic.py, escape for escape.
  const SEPARATORS =
    " \t\n\r\v\f" +
    "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~" +
    "\u00a0\u00ab\u00bb\u2010\u2011\u2012\u2013\u2014\u2015" +
    "\u2018\u2019\u201a\u201b\u201c\u201d\u201e\u201f" +
    "\u2026\u2030\u2032\u2033\u2039\u203a\u2044" +
    "\u3000\u300c\u300d\u300e\u300f" +
    "\uff01\uff02\uff08\uff09\uff0c\uff0e\uff1a\uff1b\uff1f";
  const SEPARATOR_SET = new Set(Array.from(SEPARATORS));

  const NGRAM_WIDTHS = [3, 4];
  const PAD_START = "\u0002";
  const PAD_END = "\u0003";
  const MAX_SKETCH_INPUT = 4000;

  const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

  function normalize(value) {
    const lowered = String(value).toLowerCase();
    let out = "";
    let previousSpace = true;
    for (const character of lowered) {
      if (SEPARATOR_SET.has(character)) {
        if (!previousSpace) {
          out += " ";
          previousSpace = true;
        }
        continue;
      }
      out += character;
      previousSpace = false;
    }
    if (out.endsWith(" ")) out = out.slice(0, -1);
    return out;
  }

  function features(normalized) {
    const collected = [];
    for (const token of normalized.split(" ")) {
      if (!token) continue;
      collected.push("w:" + token);
      // Array.from gives code points, matching Python's string indexing.
      const padded = Array.from(PAD_START + token + PAD_END);
      const length = padded.length;
      for (const width of NGRAM_WIDTHS) {
        if (length < width) continue;
        for (let start = 0; start <= length - width; start += 1) {
          collected.push("c:" + padded.slice(start, start + width).join(""));
        }
      }
    }
    return collected;
  }

  // FNV-1a over the UTF-8 bytes of the string, without materializing the bytes.
  function fnv1a32(value) {
    let digest = FNV_OFFSET_BASIS >>> 0;
    const mix = byte => {
      digest = Math.imul((digest ^ byte) >>> 0, FNV_PRIME) >>> 0;
    };
    for (const character of value) {
      let code = character.codePointAt(0);
      // A lone surrogate cannot reach here from Python, and TextEncoder would
      // substitute it, so this file substitutes it the same way.
      if (code >= 0xd800 && code <= 0xdfff) code = 0xfffd;
      if (code < 0x80) {
        mix(code);
      } else if (code < 0x800) {
        mix(0xc0 | (code >> 6));
        mix(0x80 | (code & 0x3f));
      } else if (code < 0x10000) {
        mix(0xe0 | (code >> 12));
        mix(0x80 | ((code >> 6) & 0x3f));
        mix(0x80 | (code & 0x3f));
      } else {
        mix(0xf0 | (code >> 18));
        mix(0x80 | ((code >> 12) & 0x3f));
        mix(0x80 | ((code >> 6) & 0x3f));
        mix(0x80 | (code & 0x3f));
      }
    }
    return digest >>> 0;
  }

  function roundHalfAwayFromZero(value) {
    return value >= 0 ? Math.floor(value + 0.5) : Math.ceil(value - 0.5);
  }

  function project(collected) {
    const vector = new Array(SKETCH_DIMENSIONS).fill(0);
    for (const feature of collected) {
      const digest = fnv1a32(feature);
      const index = digest % SKETCH_DIMENSIONS;
      if ((digest >>> 8) & 1) vector[index] -= 1;
      else vector[index] += 1;
    }
    let total = 0;
    for (const component of vector) total += component * component;
    if (total <= 0) return null;
    const norm = Math.sqrt(total);
    const quantized = new Array(SKETCH_DIMENSIONS);
    for (let index = 0; index < SKETCH_DIMENSIONS; index += 1) {
      // Same association as Python: (component / norm) * 127.
      let scaled = roundHalfAwayFromZero((vector[index] / norm) * 127);
      if (scaled > 127) scaled = 127;
      else if (scaled < -127) scaled = -127;
      quantized[index] = scaled;
    }
    return quantized;
  }

  function base64(bytes) {
    let out = "";
    for (let index = 0; index < bytes.length; index += 3) {
      const first = bytes[index];
      const second = index + 1 < bytes.length ? bytes[index + 1] : 0;
      const third = index + 2 < bytes.length ? bytes[index + 2] : 0;
      out += BASE64_ALPHABET[first >> 2];
      out += BASE64_ALPHABET[((first & 0x03) << 4) | (second >> 4)];
      out += index + 1 < bytes.length ? BASE64_ALPHABET[((second & 0x0f) << 2) | (third >> 6)] : "=";
      out += index + 2 < bytes.length ? BASE64_ALPHABET[third & 0x3f] : "=";
    }
    return out;
  }

  function unbase64(value) {
    const clean = String(value).replace(/=+$/, "");
    if (/[^A-Za-z0-9+/]/.test(clean)) return null;
    const bytes = [];
    let buffer = 0;
    let bits = 0;
    for (const character of clean) {
      buffer = (buffer << 6) | BASE64_ALPHABET.indexOf(character);
      bits += 6;
      if (bits >= 8) {
        bits -= 8;
        bytes.push((buffer >> bits) & 0xff);
      }
    }
    return bytes;
  }

  /** Sketch one or more strings, or null when there is nothing to sketch. */
  function sketch(...parts) {
    const joined = parts.filter(part => part).join(" ");
    if (!joined) return null;
    const bounded = joined.length > MAX_SKETCH_INPUT ? joined.slice(0, MAX_SKETCH_INPUT) : joined;
    const quantized = project(features(normalize(bounded)));
    if (quantized === null) return null;
    const bytes = quantized.map(component => (component + 256) % 256);
    return SKETCH_VERSION + ":" + base64(bytes);
  }

  /** Decode a stored sketch, or null when it is absent, malformed or another version. */
  function decode(value) {
    if (!value) return null;
    const separator = String(value).indexOf(":");
    if (separator < 0) return null;
    if (String(value).slice(0, separator) !== SKETCH_VERSION) return null;
    const bytes = unbase64(String(value).slice(separator + 1));
    if (bytes === null || bytes.length !== SKETCH_DIMENSIONS) return null;
    return bytes.map(byte => (byte > 127 ? byte - 256 : byte));
  }

  /** The distinct features of a string, for containment rather than cosine. */
  function featureSet(value) {
    if (!value) return new Set();
    const bounded = value.length > MAX_SKETCH_INPUT ? value.slice(0, MAX_SKETCH_INPUT) : value;
    return new Set(features(normalize(bounded)));
  }

  /**
   * How much of `phrase` appears in `document`, in [0, 1].
   *
   * Asymmetric on purpose. Cosine over whole-document sketches was tried first
   * for the viewer's personal filter and measured not to separate at all: a
   * short phrase against a long body is divided down by the body's whole feature
   * mass, so an exact appearance and an accidental one land in the same range.
   * Containment asks what the viewer is actually asking -- does what I wrote
   * appear in this. app/semantic.py carries the numbers.
   */
  function containment(phrase, document) {
    const phraseFeatures = featureSet(phrase);
    if (phraseFeatures.size === 0) return 0;
    const documentFeatures = featureSet(document);
    if (documentFeatures.size === 0) return 0;
    let shared = 0;
    for (const feature of phraseFeatures) {
      if (documentFeatures.has(feature)) shared += 1;
    }
    return shared / phraseFeatures.size;
  }

  function similarityVectors(left, right) {
    let dot = 0;
    let leftSquare = 0;
    let rightSquare = 0;
    for (let index = 0; index < left.length; index += 1) {
      dot += left[index] * right[index];
      leftSquare += left[index] * left[index];
      rightSquare += right[index] * right[index];
    }
    if (leftSquare <= 0 || rightSquare <= 0) return 0;
    return dot / Math.sqrt(leftSquare * rightSquare);
  }

  /** Cosine of two stored sketches, or 0 when either side is unusable. */
  function similarity(left, right) {
    const leftVector = decode(left);
    const rightVector = decode(right);
    if (leftVector === null || rightVector === null) return 0;
    return similarityVectors(leftVector, rightVector);
  }

  const api = {
    SKETCH_VERSION,
    SKETCH_DIMENSIONS,
    sketch,
    decode,
    similarity,
    similarityVectors,
    featureSet,
    containment,
  };

  if (typeof window !== "undefined") window.LouderrSimilarity = api;
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (typeof globalThis !== "undefined") globalThis.LouderrSimilarity = api;
})();
